import React from "react";
import Head from "next/head";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";
import { stringify } from "qs";

import useAlternateUrl from "@/hooks/useAlternateUrl";
import useCanonical from "@/hooks/useCanonical";
import useDomain from "@/hooks/useDomain";

const MetaTags: React.FC<{
  canonicalParams?: string[];
  canonicalOverride?: string;
  children?: React.ReactNode;
  description?: string;
  endcodeCanonicalParams?: Parameters<typeof stringify>[1];
  image?: string;
  preserveParamOrder?: boolean;
  robots?: string;
  shouldShowAlternates?: boolean;
  title: string;
  baseUrl?: string;
}> = ({
  canonicalParams = [],
  canonicalOverride,
  children,
  description,
  endcodeCanonicalParams,
  image = "https://public.leafly.com/web-home/logo-1200x630.png",
  preserveParamOrder = false,
  robots,
  shouldShowAlternates = true,
  title,
}) => {
  const { asPath } = useRouter();

  let canonicalUrl = useCanonical({
    encodeParams: endcodeCanonicalParams,
    params: canonicalParams,
    preserveParamOrder: preserveParamOrder,
  });
  const { usUrl, caUrl } = useAlternateUrl();
  const domain = useDomain();

  if (canonicalOverride) {
    canonicalUrl = `${domain}${canonicalOverride}`;
  }

  const isNoindex = !!robots?.includes("noindex");
  const shouldShowCanonical = canonicalUrl && !isNoindex;
  const showAlternate = shouldShowAlternates && !isNoindex;

  return (
    <Head>
      <title>{title}</title>
      {shouldShowCanonical && <link rel="canonical" href={canonicalUrl} />}
      {robots && <meta name="robots" content={robots} />}
      {description && <meta name="description" content={description} />}
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={`${domain}${asPath}`} />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:type" content="webpage" />

      {showAlternate && (
        <>
          <link href={`${usUrl}`} hrefLang="en-us" rel="alternate" />
          <link href={`${caUrl}`} hrefLang="en-ca" rel="alternate" />
        </>
      )}

      {children}
    </Head>
  );
};

export default MetaTags;
